import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IHiringColumn, IHiringFilters } from './types';

export interface HiringState {
  columns: IHiringColumn[];
  isLoading: boolean;
  loadingCardIds: string[];
  isDragging: boolean;
  filters: IHiringFilters;
  editTaskModal: { isOpen: boolean; candidateId: string; taskId: string } | null;
}

const initialState: HiringState = {
  columns: [],
  isLoading: true,
  loadingCardIds: [],
  isDragging: false,
  filters: {
    statusFilter: 1, 
    sortByFilter: 3, 
    candidatesFilter: 1, 
    scoreSelectorFilter: null,
    searchQuery: '',
    favoritesOnly: false,
    dateRange: { start: null, end: null }
  },
  editTaskModal: null,
};

const hiringSlice = createSlice({
  name: 'hiring',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setCardLoading(state, action: PayloadAction<{ cardId: string; isLoading: boolean }>) {
      const { cardId, isLoading } = action.payload;
      if (isLoading && !state.loadingCardIds.includes(cardId)) {
        state.loadingCardIds.push(cardId);
      } else if (!isLoading) {
        state.loadingCardIds = state.loadingCardIds.filter((id) => id !== cardId);
      }
    },
    setBoardColumns(state, action: PayloadAction<IHiringColumn[]>) {
      state.columns = action.payload;
      state.isLoading = false;
    },
    setFilter(state, action: PayloadAction<{ key: keyof IHiringFilters; value: any }>) {
      const { key, value } = action.payload;
      // @ts-ignore
      state.filters[key] = value;
    },
    toggleDragging(state, action: PayloadAction<boolean>) {
      state.isDragging = action.payload;
    },
    // FIX 7c: Move by ID, not array index
    moveCardOptimistic(
      state,
      action: PayloadAction<{
        cardId: string;
        sourceColumnId: string;
        destColumnId: string;
        destCardIndex: number;
      }>
    ) {
      const { cardId, sourceColumnId, destColumnId, destCardIndex } = action.payload;
      
      const sourceCol = state.columns.find(c => c.id === sourceColumnId);
      const destCol = state.columns.find(c => c.id === destColumnId);
      
      if (!sourceCol || !destCol) return;

      const cardIndex = sourceCol.cards.findIndex(c => c.id === cardId);
      if (cardIndex === -1) return;

      const [movedCard] = sourceCol.cards.splice(cardIndex, 1);
      destCol.cards.splice(destCardIndex, 0, movedCard);
    },
    revertCardMove(
      state,
      action: PayloadAction<{
        cardId: string;
        sourceColumnId: string;
        destColumnId: string;
        originalCardIndex: number;
      }>
    ) {
      const { cardId, sourceColumnId, destColumnId, originalCardIndex } = action.payload;
      
      const sourceCol = state.columns.find(c => c.id === sourceColumnId);
      const destCol = state.columns.find(c => c.id === destColumnId);
      
      if (!sourceCol || !destCol) return;

      const cardIndex = destCol.cards.findIndex(c => c.id === cardId);
      if (cardIndex === -1) return;

      const [revertedCard] = destCol.cards.splice(cardIndex, 1);
      sourceCol.cards.splice(originalCardIndex, 0, revertedCard);
    },
    toggleCandidateFavorite(state, action: PayloadAction<{ candidateId: string, isFavorite: boolean }>) {
      const { candidateId, isFavorite } = action.payload;
      for (const col of state.columns) {
        const card = col.cards.find(c => c.id === candidateId);
        if (card) {
          card.isFavorite = isFavorite;
          break;
        }
      }
    },
    openEditTaskModal(state, action: PayloadAction<{ candidateId: string; taskId: string }>) {
      state.editTaskModal = { isOpen: true, ...action.payload };
    },
    closeEditTaskModal(state) {
      state.editTaskModal = null;
    },
    approveCandidate(state, action: PayloadAction<{ jobId: string; candidateId: string; taskId: string }>) {
    },
    disqualifyCandidate(state, action: PayloadAction<{ candidateId: string; taskId: string }>) {
       // A Saga will intercept this to make the API call.
       // Optimistically mark the candidate as disqualified in the UI:
       for (const col of state.columns) {
         const card = col.cards.find(c => c.id === action.payload.candidateId);
         if (card) {
           card.disqualified = true;
           break;
         }
       }
    },
    requalifyCandidate(state, action: PayloadAction<{ candidateId: string }>) {
       for (const col of state.columns) {
         const card = col.cards.find(c => c.id === action.payload.candidateId);
         if (card) {
           card.disqualified = false;
           break;
         }
       }
    }
  },
});

export const {
  setLoading,
  setCardLoading,
  setBoardColumns,
  setFilter,
  toggleDragging,
  moveCardOptimistic,
  revertCardMove,
  toggleCandidateFavorite,
  openEditTaskModal,
  closeEditTaskModal,
  approveCandidate,
  disqualifyCandidate,
  requalifyCandidate
} = hiringSlice.actions;

export default hiringSlice.reducer;